import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Operates in two modes:
//
//   API mode  (default, no data-date-navigation-base-url-value set)
//     PUTs to /api/v1/selected_date which writes user.selected_date and
//     turbo-streams the result. Used on /time_activities.
//
//   URL mode  (when data-date-navigation-base-url-value is set, e.g. "/my-team")
//     Computes the target date client-side and Turbo.visits
//     `${baseUrl}?date=YYYY-MM-DD` (or `${baseUrl}` for today). Does NOT
//     write user.selected_date — keeps the host page's date isolated.
export default class extends Controller {
  static targets = ["date"]
  static values = { baseUrl: { type: String, default: "" } }

  get urlMode() {
    return this.baseUrlValue !== ""
  }

  currentDate() {
    return this.hasDateTarget ? this.dateTarget.dataset.date : null
  }

  previous() {
    if (this.urlMode) {
      this.urlVisit(this.shiftDate(this.currentDate(), -1))
    } else {
      this.navigate({ previous_day: true, date: this.currentDate() })
    }
  }

  next() {
    if (this.urlMode) {
      this.urlVisit(this.shiftDate(this.currentDate(), 1))
    } else {
      this.navigate({ next_day: true, date: this.currentDate() })
    }
  }

  setToday() {
    if (this.urlMode) {
      // Pass today explicitly. Navigating to baseUrl with no ?date= would
      // fall back to user.displayed_date, which may be set to a different
      // date from /time_activities and wouldn't actually land on today.
      this.urlVisit(this.todayISO())
    } else {
      this.navigate({ set_today: true })
    }
  }

  selectDate(event) {
    const date = event.currentTarget.dataset.date
    if (this.urlMode) {
      this.urlVisit(date)
    } else {
      this.navigate({ date })
    }
  }

  // --- URL mode helpers ---------------------------------------------

  urlVisit(date) {
    const url = date ? `${this.baseUrlValue}?date=${date}` : this.baseUrlValue
    Turbo.visit(url, { action: "replace" })
  }

  todayISO() {
    const dt = new Date()
    const y = dt.getFullYear()
    const m = String(dt.getMonth() + 1).padStart(2, '0')
    const d = String(dt.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  // YYYY-MM-DD ± days, returns YYYY-MM-DD. Parsed in local time so the
  // user's wall-clock day boundaries are respected.
  shiftDate(iso, deltaDays) {
    if (!iso) return null
    const [y, m, d] = iso.split('-').map(Number)
    const dt = new Date(y, m - 1, d)
    dt.setDate(dt.getDate() + deltaDays)
    const yy = dt.getFullYear()
    const mm = String(dt.getMonth() + 1).padStart(2, '0')
    const dd = String(dt.getDate()).padStart(2, '0')
    return `${yy}-${mm}-${dd}`
  }

  // --- API mode (original behavior) ---------------------------------

  async navigate(params) {
    try {
      const response = await fetch('/api/v1/selected_date', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(params)
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const text = await response.text()
      Turbo.renderStreamMessage(text)

      // Notify other pages that the user-wide selected_date just changed.
      this.dispatch("changed", { detail: { params }, bubbles: true })
    } catch (error) {
      console.error('Error:', error)
    }
  }
}
